import { EventObject } from '@xstate/store-react';
import { InitOptions } from '@domgen/checkout-sdk-types';
import posthog from 'posthog-js';
import { isCheckoutPageVariant, PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { log } from '@domgen/shared/utils/log';

/**
 * Stores prepared checkout initialization data and clears loading state.
 * Also assigns the checkout page experiment variant, kept apart from the quote page experiment
 * so both can be reported on the same events.
 *
 * @param context Current store context.
 * @param event Event containing checkout init options.
 * @returns Updated context with checkout payload and checkout experiment tracking.
 */
export const setCheckout = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: InitOptions },
): StoreContext => {
  log.debug({ message: StoreEvent.SET_CHECKOUT, data: event.data });

  // This is the call that reports the PostHog exposure ($feature_flag_called), so it must stay
  // on the path every user takes into checkout.
  const flagResponse = posthog.getFeatureFlag(PosthogFeatureFlags.CHECKOUT_PAGE);
  const checkoutVariant = isCheckoutPageVariant(flagResponse) ? flagResponse : undefined;

  if (flagResponse && !checkoutVariant) {
    log.warn({
      message:
        'Unrecognised checkout page experiment variant from PostHog, falling back to the control layout',
      data: { flag: PosthogFeatureFlags.CHECKOUT_PAGE, response: flagResponse },
    });
  }

  return {
    ...context,
    checkout: {
      loading: false,
      data: event.data,
    },
    gaEventData: {
      ...context.gaEventData,
      checkoutExperimentName: checkoutVariant ? PosthogFeatureFlags.CHECKOUT_PAGE : undefined,
      checkoutExperimentVariant: checkoutVariant,
    },
  };
};
